import { ChevronDown, ChevronRight, FolderTree, Layers, Plus, Settings, Target } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CreateButton } from '../../../../components/CreateButton';
import { EmptyState } from '../../../../components/EmptyState';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { SearchGroup } from '../../../../components/index-page/SearchGroup';
import { RowActionMenu } from '../../../../components/table/RowActionMenu';
import { Tooltip } from '../../../../components/Tooltip';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { goalSectorsApi, goalSubSectorsApi, goalsApi } from '../budgetGoalsApi';
import type {
  GoalSectorTreeNode,
  GoalSubSectorTreeNode,
  GoalTreeNode,
} from '../budgetGoalTypes';
import { GoalModal } from './GoalModal';
import { GoalSectorModal } from './GoalSectorModal';
import { GoalSubSectorModal } from './GoalSubSectorModal';

/**
 * Goal Setting — the whole SECTOR → SUB-SECTOR → GOAL catalog, as a tree-grid.
 *
 * **Captured from the reference's own `AbpGoalsTab` on 23 Sep 2026, which is the shape this
 * product had reinvented as three separate index pages under Settings.** Its own comment says
 * what it replaced: *"this tab is the catalog's home — it no longer defers to Settings ›
 * Goals."* Each sector is a parent row that expands to its sub-sectors, each sub-sector
 * expands to its goals, and each goal leaf carries its Objective and Strategy as columns.
 *
 * **Every row's `+` adds its own CHILD** — a sector adds a sub-sector, a sub-sector adds a
 * goal — and Add Sector sits in the toolbar. That is what makes the tree an editor rather than
 * a picture of one: the reader never leaves to a settings page to add the rung they are
 * looking at.
 *
 * **It is the CATALOG, not this plan's selection.** Product call, 23 Sep 2026: a plan adopted
 * a subset and a PPA was refused a goal outside it; that join is gone, so a goal created under
 * any sub-sector is available to every plan whatever its fiscal year. The tab takes no plan id
 * for exactly that reason — and the consequence is stated rather than discovered: an edit made
 * while looking at FY2033 changes what FY2034 shows, because there is one framework.
 *
 * **Reuses the three catalog modals rather than growing its own.** `<GoalSectorModal>`,
 * `<GoalSubSectorModal>` and `<GoalModal>` already lock a parent when they are opened from
 * one, which is exactly what a `+` on a row needs; their `editing` prop was widened to the
 * fields they read so a tree node satisfies it.
 */
export function GoalSettingPanel() {
  const canCreate = usePermission('budget-goals.create');
  const canUpdate = usePermission('budget-goals.update');
  const canDelete = usePermission('budget-goals.delete');

  const [tree, setTree] = useState<GoalSectorTreeNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  /*
    Show deleted keeps retired rows VISIBLE rather than dropping them, which is what makes
    Restore reachable at all: a row nothing lists is a row nobody can put back. It is the
    settings cog's entry here exactly as it is on every index page, and it is NOT persisted —
    a view that quietly kept showing deleted rows is how one gets treated as live.
  */
  const [showDeleted, setShowDeleted] = useState(false);

  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(new Set());
  const [expandedSubs, setExpandedSubs] = useState<Set<string>>(new Set());

  type SectorTarget = { editing: GoalSectorTreeNode | null };
  type SubTarget = {
    editing: GoalSubSectorTreeNode | null;
    sector: GoalSectorTreeNode;
  };
  type GoalTarget = {
    editing: GoalTreeNode | null;
    sector: GoalSectorTreeNode;
    sub: GoalSubSectorTreeNode;
  };
  type DeleteTarget = { kind: 'sector' | 'sub' | 'goal'; id: string; name: string; note: string };

  const [sectorTarget, setSectorTarget] = useState<SectorTarget | null>(null);
  const [subTarget, setSubTarget] = useState<SubTarget | null>(null);
  const [goalTarget, setGoalTarget] = useState<GoalTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const rows = await goalSectorsApi.tree(showDeleted, signal);
        if (signal?.aborted) return;

        setTree(rows);
        setError(null);
      } catch (caught) {
        if (signal?.aborted) return;

        setTree([]);
        setError(
          caught instanceof ApiError ? caught.message : 'Could not load the goal framework.',
        );
      }
    },
    [showDeleted],
  );

  /*
    EXPANDED ON ARRIVAL, every rung. A framework the reader has to open one row at a time
    answers nothing at a glance, and this is the surface they come to to see its shape. The
    reference does the same on its first load.

    Only on the FIRST load: re-expanding after every save would reopen rows the reader had
    deliberately collapsed while working down a long sector.
  */
  const expandedOnce = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    void load(controller.signal).then(() => {
      if (controller.signal.aborted || expandedOnce.current) return;
      expandedOnce.current = true;
    });

    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (tree === null || expandedSectors.size > 0 || expandedSubs.size > 0) return;
    if (tree.length === 0) return;

    setExpandedSectors(new Set(tree.map((s) => s.id)));
    setExpandedSubs(new Set(tree.flatMap((s) => s.subSectors.map((ss) => ss.id))));
  }, [tree, expandedSectors.size, expandedSubs.size]);

  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (!next.delete(id)) next.add(id);
    return next;
  };

  /*
    Searched IN MEMORY, and that is the tree exemption rather than the paged-list defect: the
    catalog arrives WHOLE, so the filter sees every row there is. A matching SECTOR keeps its
    entire subtree — a hit is usually the branch the reader was looking for, and stripping its
    children answers "where is Health" with a row that no longer says what is under it.
  */
  const filtered = useMemo<GoalSectorTreeNode[]>(() => {
    const term = search.trim().toLowerCase();
    if (term.length === 0 || tree === null) return tree ?? [];

    const hit = (...values: (string | null)[]) =>
      values.some((v) => (v ?? '').toLowerCase().includes(term));

    return tree
      .map((sector) => {
        if (hit(sector.name, sector.description)) return sector;

        const subSectors = sector.subSectors
          .map((sub) => {
            if (hit(sub.name, sub.description)) return sub;

            const goals = sub.goals.filter((g) => hit(g.name, g.objective, g.strategy));
            return goals.length > 0 ? { ...sub, goals } : null;
          })
          .filter((s): s is GoalSubSectorTreeNode => s !== null);

        return subSectors.length > 0 ? { ...sector, subSectors } : null;
      })
      .filter((s): s is GoalSectorTreeNode => s !== null);
  }, [tree, search]);

  const goalCount = useMemo(
    () => (tree ?? []).reduce((n, s) => n + s.subSectors.reduce((m, ss) => m + ss.goals.length, 0), 0),
    [tree],
  );

  async function runDelete() {
    if (!deleteTarget) return;

    const { kind, id } = deleteTarget;

    if (kind === 'sector') await goalSectorsApi.deactivate(id);
    else if (kind === 'sub') await goalSubSectorsApi.deactivate(id);
    else await goalsApi.deactivate(id);

    setDeleteTarget(null);
    await load();
  }

  async function restore(kind: 'sector' | 'sub' | 'goal', id: string) {
    if (kind === 'sector') await goalSectorsApi.activate(id);
    else if (kind === 'sub') await goalSubSectorsApi.activate(id);
    else await goalsApi.activate(id);

    await load();
  }

  const settingsItems: MenuItem[] = [
    {
      key: 'show-deleted',
      label: 'Show deleted',
      checked: showDeleted,
      onClick: () => setShowDeleted((v) => !v),
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      {/*
        The index page's own strip — search, the record count, the settings cog — because a
        catalog is still a list of records and a reader should not meet a second shape for it.
        The count is GOALS: a framework is measured by what it can be aligned to, and the
        sectors above them are how those are filed.
      */}
      <div className="flex min-w-0 shrink-0 items-center gap-2">
        {/*
          The shared button. It reads the key itself through `<PermissionGate>`, so the panel no
          longer branches on `canCreate` here — and the icon cannot be got wrong, which is what
          this exact button did once: passed as a CHILD it landed inside `<Button>`'s label span,
          where Tailwind preflight's `svg { display: block }` pushed the label onto a second line.
        */}
        <CreateButton
          id="goal-setting-add-sector"
          onClick={() => setSectorTarget({ editing: null })}
          label="Add sector"
          permission="budget-goals.create"
        />

        <SearchGroup
          id="goal-setting"
          className="ml-auto flex-1"
          value={search}
          onChange={setSearch}
          placeholder="Search sectors, sub-sectors and goals"
          total={goalCount}
        >
          <Menu
            id="goal-setting-settings-menu"
            items={settingsItems}
            trigger={({ open, toggle }) => (
              <Tooltip label="Goal Setting options">
                <button
                  type="button"
                  id="goal-setting-settings"
                  onClick={toggle}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  aria-label="Goal Setting options"
                  className="flex h-full items-center rounded-r-lg border-l border-slate-300 px-3 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200"
                >
                  <Settings className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>
            )}
          />
        </SearchGroup>
      </div>

      <div className="modern-scrollbar min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200">
        {tree === null ? (
          <div className="p-4 text-xs text-slate-500">Loading the goal framework…</div>
        ) : error !== null ? (
          <EmptyState message={error} />
        ) : filtered.length === 0 ? (
          <EmptyState
            message={
              search.trim().length > 0
                ? 'No sector, sub-sector or goal matches that search.'
                : 'No sectors yet. Add one to start this programme’s goal framework — a PPA can only align to a goal, and a goal is filed under a sub-sector of a sector.'
            }
          />
        ) : (
          <table className="w-full table-fixed border-collapse text-sm">
            <colgroup>
              <col />
              <col style={{ width: 280 }} />
              <col style={{ width: 280 }} />
              <col style={{ width: 190 }} />
            </colgroup>

            {/*
              The product's own header treatment — the tinted band, 9px uppercase, and the
              weight on the CELLS rather than the row, because a `<th>` carries the browser's
              own bold and a weight set on the `<tr>` never reaches it.
            */}
            <thead className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>tr>th]:font-normal">
              <tr>
                <th className="border-b border-slate-300 px-3 py-1.5 text-left">
                  Sector / Sub-sector / Goal
                </th>
                <th className="border-b border-l border-slate-300 px-3 py-1.5 text-left">
                  Objective
                </th>
                <th className="border-b border-l border-slate-300 px-3 py-1.5 text-left">
                  Strategy
                </th>
                <th className="border-b border-l border-slate-300 px-3 py-1.5 text-left">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((sector) => {
                const sectorOpen = expandedSectors.has(sector.id);
                const liveSubs = sector.subSectors.filter((s) => s.isActive).length;

                return (
                  <TreeRows key={sector.id}>
                    <TreeRow
                      depth={0}
                      muted={!sector.isActive}
                      icon={<FolderTree className="h-4 w-4 shrink-0 text-blue-600" aria-hidden />}
                      open={sectorOpen}
                      onToggle={() => setExpandedSectors((p) => toggle(p, sector.id))}
                      name={sector.name}
                      count={`${liveSubs} sub-sector${liveSubs === 1 ? '' : 's'}`}
                      addLabel="Add sub-sector"
                      onAdd={
                        canCreate && sector.isActive
                          ? () => {
                              if (!sectorOpen) setExpandedSectors((p) => toggle(p, sector.id));
                              setSubTarget({ editing: null, sector });
                            }
                          : undefined
                      }
                      menu={
                        <RowActionMenu
                          id={`goal-sector-${sector.id}`}
                          otherInfo={null}
                          onEdit={
                            canUpdate && sector.isActive
                              ? () => setSectorTarget({ editing: sector })
                              : undefined
                          }
                          editPermission="budget-goals.update"
                          onDelete={
                            canDelete && sector.isActive
                              ? () =>
                                  setDeleteTarget({
                                    kind: 'sector',
                                    id: sector.id,
                                    name: sector.name,
                                    note: 'Its sub-sectors and goals are left exactly as they are — they stop being offered on a fresh PPA and stay on every one already filed against them.',
                                  })
                              : undefined
                          }
                          deletePermission="budget-goals.delete"
                          extraItems={
                            !sector.isActive && canDelete
                              ? [
                                  {
                                    key: 'restore',
                                    label: 'Restore',
                                    permission: 'budget-goals.delete',
                                    onClick: () => void restore('sector', sector.id),
                                  },
                                ]
                              : []
                          }
                        />
                      }
                    />

                    {sectorOpen
                      ? sector.subSectors.map((sub) => {
                          const subOpen = expandedSubs.has(sub.id);
                          const liveGoals = sub.goals.filter((g) => g.isActive).length;

                          return (
                            <TreeRows key={sub.id}>
                              <TreeRow
                                depth={1}
                                muted={!sub.isActive}
                                icon={<Layers className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />}
                                open={subOpen}
                                onToggle={() => setExpandedSubs((p) => toggle(p, sub.id))}
                                name={sub.name}
                                count={`${liveGoals} goal${liveGoals === 1 ? '' : 's'}`}
                                addLabel="Add goal"
                                onAdd={
                                  canCreate && sub.isActive
                                    ? () => {
                                        if (!subOpen) setExpandedSubs((p) => toggle(p, sub.id));
                                        setGoalTarget({ editing: null, sector, sub });
                                      }
                                    : undefined
                                }
                                menu={
                                  <RowActionMenu
                                    id={`goal-sub-sector-${sub.id}`}
                                    otherInfo={null}
                                    onEdit={
                                      canUpdate && sub.isActive
                                        ? () => setSubTarget({ editing: sub, sector })
                                        : undefined
                                    }
                                    editPermission="budget-goals.update"
                                    onDelete={
                                      canDelete && sub.isActive
                                        ? () =>
                                            setDeleteTarget({
                                              kind: 'sub',
                                              id: sub.id,
                                              name: sub.name,
                                              note: 'Its goals are left exactly as they are — they stop being offered on a fresh PPA and stay on every one already filed against them.',
                                            })
                                        : undefined
                                    }
                                    deletePermission="budget-goals.delete"
                                    extraItems={
                                      !sub.isActive && canDelete
                                        ? [
                                            {
                                              key: 'restore',
                                              label: 'Restore',
                                              permission: 'budget-goals.delete',
                                              onClick: () => void restore('sub', sub.id),
                                            },
                                          ]
                                        : []
                                    }
                                  />
                                }
                              />

                              {subOpen && sub.goals.length === 0 ? (
                                <tr className="border-t border-slate-200">
                                  <td
                                    colSpan={4}
                                    className="px-3 py-2 text-xs text-slate-400"
                                    style={{ paddingLeft: indent(2) }}
                                  >
                                    No goals under this sub-sector yet.
                                  </td>
                                </tr>
                              ) : null}

                              {subOpen
                                ? sub.goals.map((goal) => (
                                    <tr
                                      key={goal.id}
                                      className={`group border-t border-slate-200 align-top hover:bg-slate-50 ${
                                        goal.isActive ? '' : 'opacity-60'
                                      }`}
                                    >
                                      <td
                                        className="px-3 py-2 align-top"
                                        style={{ paddingLeft: indent(2) }}
                                      >
                                        <div className="flex items-start gap-2">
                                          <Target
                                            className="mt-0.5 h-4 w-4 shrink-0 text-slate-300"
                                            aria-hidden
                                          />
                                          <span className="break-words text-slate-700">
                                            {goal.name}
                                          </span>
                                          {!goal.isActive ? (
                                            <span className="shrink-0 text-[11px] text-slate-400">
                                              · Deleted
                                            </span>
                                          ) : null}
                                        </div>
                                      </td>
                                      <td className="break-words border-l border-slate-200 px-3 py-2 align-top text-slate-600">
                                        {goal.objective}
                                      </td>
                                      <td className="break-words border-l border-slate-200 px-3 py-2 align-top text-slate-600">
                                        {goal.strategy ?? '—'}
                                      </td>
                                      <td className="border-l border-slate-200 px-3 py-2 align-middle">
                                        <div className="flex justify-end">
                                          <RowActionMenu
                                            id={`goal-${goal.id}`}
                                            otherInfo={null}
                                            onEdit={
                                              canUpdate && goal.isActive
                                                ? () => setGoalTarget({ editing: goal, sector, sub })
                                                : undefined
                                            }
                                            editPermission="budget-goals.update"
                                            onDelete={
                                              canDelete && goal.isActive
                                                ? () =>
                                                    setDeleteTarget({
                                                      kind: 'goal',
                                                      id: goal.id,
                                                      name: goal.name,
                                                      note: 'Every PPA already aligned to it keeps it; it stops being offered on a fresh one.',
                                                    })
                                                : undefined
                                            }
                                            deletePermission="budget-goals.delete"
                                            extraItems={
                                              !goal.isActive && canDelete
                                                ? [
                                                    {
                                                      key: 'restore',
                                                      label: 'Restore',
                                                      permission: 'budget-goals.delete',
                                                      onClick: () => void restore('goal', goal.id),
                                                    },
                                                  ]
                                                : []
                                            }
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                : null}
                            </TreeRows>
                          );
                        })
                      : null}
                  </TreeRows>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {sectorTarget ? (
        <GoalSectorModal
          open
          editing={sectorTarget.editing}
          onClose={() => setSectorTarget(null)}
          onSaved={() => {
            setSectorTarget(null);
            void load();
          }}
        />
      ) : null}

      {subTarget ? (
        <GoalSubSectorModal
          open
          editing={
            subTarget.editing
              ? {
                  ...subTarget.editing,
                  goalSectorId: subTarget.sector.id,
                  sectorName: subTarget.sector.name,
                }
              : null
          }
          /*
            LOCKED to the row the `+` was pressed on — the reader chose the parent by choosing
            whose row to press, so the picker has nothing left to ask. The org chart's
            locked-parent rule, and the name travels with the id so the field states it from
            the first paint rather than showing its placeholder while a feed arrives.
          */
          lockedSector={
            subTarget.editing ? undefined : { id: subTarget.sector.id, name: subTarget.sector.name }
          }
          onClose={() => setSubTarget(null)}
          onSaved={() => {
            setSubTarget(null);
            void load();
          }}
        />
      ) : null}

      {goalTarget ? (
        <GoalModal
          open
          editing={
            goalTarget.editing
              ? {
                  ...goalTarget.editing,
                  goalSubSectorId: goalTarget.sub.id,
                  subSectorName: goalTarget.sub.name,
                  sectorName: goalTarget.sector.name,
                }
              : null
          }
          lockedSubSector={
            goalTarget.editing
              ? undefined
              : {
                  id: goalTarget.sub.id,
                  name: goalTarget.sub.name,
                  sectorName: goalTarget.sector.name,
                }
          }
          onClose={() => setGoalTarget(null)}
          onSaved={() => {
            setGoalTarget(null);
            void load();
          }}
        />
      ) : null}

      {deleteTarget ? (
        <ConfirmDialog
          open
          id="goal-setting-delete"
          title={`Delete ${deleteTarget.name}?`}
          /*
            A deleted catalog row is REFUSED on a fresh write and LEFT ALONE where it is
            already recorded, so the delete does less than its label suggests — and the body
            says so before the tick rather than leaving the reader to notice nothing changed.
          */
          body={`${deleteTarget.note} It is kept and can be restored.`}
          danger
          requireConfirm
          onConfirm={runDelete}
          onClose={() => setDeleteTarget(null)}
        />
      ) : null}
    </div>
  );
}

/** Each rung is inset by one step, so the hierarchy is legible without a second column. */
function indent(depth: number) {
  return 12 + depth * 22;
}

/** A fragment that may hold several `<tr>`s — `<tbody>` accepts no wrapper element. */
function TreeRows({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/**
 * One PARENT row — a sector or a sub-sector.
 *
 * The count, the `+` and the kebab sit together at the trailing edge, which is the reference's
 * arrangement: the count says what is under the row, the `+` adds one more of it, and the
 * kebab acts on the row itself.
 */
function TreeRow({
  depth,
  muted,
  icon,
  open,
  onToggle,
  name,
  count,
  addLabel,
  onAdd,
  menu,
}: {
  depth: number;
  muted: boolean;
  icon: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  name: string;
  count: string;
  addLabel: string;
  /** Absent hides the control entirely — hide, never a button that opens nothing. */
  onAdd?: () => void;
  menu: React.ReactNode;
}) {
  return (
    <tr className={`border-t border-slate-200 hover:bg-slate-50 ${muted ? 'opacity-60' : ''}`}>
      <td colSpan={3} className="px-3 py-2" style={{ paddingLeft: indent(depth) }}>
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={open ? `Collapse ${name}` : `Expand ${name}`}
            className="shrink-0 rounded text-slate-400 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
          >
            {open ? (
              <ChevronDown className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden />
            )}
          </button>

          {icon}

          <span className="min-w-0 break-words font-medium text-slate-800">{name}</span>
          {muted ? <span className="shrink-0 text-[11px] text-slate-400">· Deleted</span> : null}
        </div>
      </td>

      <td className="border-l border-slate-200 px-3 py-2 align-middle">
        <div className="flex items-center justify-end gap-1">
          <span className="text-[11px] text-slate-400">{count}</span>

          {onAdd ? (
            <Tooltip label={addLabel}>
              <button
                type="button"
                onClick={onAdd}
                aria-label={addLabel}
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
              >
                <Plus className="h-4 w-4" aria-hidden />
              </button>
            </Tooltip>
          ) : null}

          {menu}
        </div>
      </td>
    </tr>
  );
}
